import { ErrorMessage } from '@hookform/error-message';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getPage, postRequest } from '../../../api/wagtail';
import { useGlobalContext } from '../../../components/GlobalContextProvider/GlobalContextProvider';
import StaticImage from '../../../components/StaticImage';
import { inputTypeMap, tagMap } from '../../../consts/formTypes';
import {
  UPGRADE_BUTTON_POST_ENDPOINT,
  UPGRADE_BUTTON_URL,
} from '../../../consts/urls';
import {
  CheckMark,
  FormField,
  Label,
  ThanksText,
  ErrorText,
} from '../formBlocks';
import {
  AlignedTextButton,
  BottomRow,
  CustomThankYouBlock,
  Description,
  Form,
  FormBody,
  ModalBody,
  ModalTitle,
  RequestUpgradeWrapper,
  StyledGreenButton,
  TopRow,
} from './RequestUpdateButton.styled';

const closeIcon = '/img/cross-icon.svg';

const RequestUpdateButton = ({ recordName, isActive, className }) => {
  const { user, contentView } = useGlobalContext();
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [isSent, setIsSent] = useState(false);
  const router = useRouter();

  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }

  const [fieldNodes, setFieldNodes] = useState([]);
  const [rawForm, setRawForm] = useState(null);

  const {
    getValues,
    watch,
    reset,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    async function fetchForm() {
      try {
        const res = await getPage(UPGRADE_BUTTON_URL);
        setRawForm(res.json.componentProps);
      } catch (e) {
        console.error(e);
      }
    }

    fetchForm();
  }, []);

  useEffect(() => {
    if (!rawForm) {
      return;
    }

    const fixedForm = rawForm.form.map((el) => ({
      ...el,
      type: inputTypeMap[el.fieldType],
      tag: tagMap[el.fieldType],
      choices:
        el.fieldType === 'dropdown'
          ? el.choices.map((el) => ({ value: el, label: el }))
          : el.choices,
    }));

    fixedForm.forEach((el) => {
      setValue(el.name, el.defaultValue);
    });

    if (user?.userId?.includes('auth0')) {
      setValue('your_name', `${user.givenName} ${user.familyName}`);
      setValue('your_email_address', user.email);
    }
    if (user?.userId?.includes('oauth2')) {
      setValue('company_name', `${user.givenName} ${user.familyName}`);
      setValue('company_email', user.email);
    }

    setValue('record_name', recordName);
    setValue('record_url', router.asPath);
    setValue('request_date', new Date().toISOString());

    setFieldNodes(fixedForm);
  }, [rawForm]);

  const submit = async () => {
    try {
      const res = await postRequest(UPGRADE_BUTTON_POST_ENDPOINT, {
        ...getValues(),
      });
      const { form } = res.json.componentProps;

      if (form.thanksText) {
        reset();
        setIsSent(true);

        window.dataLayer.push({
          userId: user?.userId,
          pageOfSubmission: recordName,
          subscriptionType: user?.userLevel,
          contentView,
          event: 'requestUpdateFormSuccess',
        });
      } else {
        for (const [key, value] of Object.entries(form.err)) {
          if (key === 'captcha') {
            toast.error('Captcha is required!');
          }

          setError(camelToSnake(key), { message: value.toString() });
        }
      }
    } catch (e) {
      toast.error('Server error');
      console.error(e);
    }
  };

  return (
    <RequestUpgradeWrapper className={className}>
      <StyledGreenButton $isActive={isActive && !isSent} onClick={openModal}>
        {'Request Update '}
      </StyledGreenButton>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          overlay: {
            backgroundColor: 'rgba(163, 178, 177, 0.60)',
          },
          content: {
            width: isSent ? '535px' : '575px',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            padding: 0,
            transform: 'translate(-50.1%, -50.1%)',
            zIndex: '2',
            backdropFilter: 'none',
          },
        }}
        contentLabel='Example Modal'
      >
        <ModalBody $isSent={isSent}>
          <TopRow>
            <StaticImage
              width={19}
              height={19}
              alt='closeIcon'
              src={closeIcon}
              onClick={closeModal}
              priority
            />
          </TopRow>
          {isSent ? (
            <CustomThankYouBlock>
              <CheckMark />
              <ThanksText>
                Thank You for your request, our editors will review it soon
              </ThanksText>
            </CustomThankYouBlock>
          ) : (
            <Form onSubmit={handleSubmit(submit)}>
              <ModalTitle>
                {rawForm ? rawForm.title : 'Describe Your Proposal'}
              </ModalTitle>
              <Description>Description</Description>

              <FormBody>
                {fieldNodes.map(
                  (el) =>
                    el.tag !== 'hidden' && (
                      <FormField
                        key={el.name}
                        $isErrored={errors[el.name]}
                        $hideLabelAfterFocus={el.tag === 'textarea'}
                      >
                        <Label
                          className={watch(el.name) && 'filled'}
                          htmlFor={el.name}
                        >
                          {el.helpText}
                        </Label>
                        <el.tag
                          type={el.type}
                          {...register(el.name, el.validation)}
                          id={el.name}
                          className={watch(el.name) && 'filled'}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={el.name}
                          render={({ message }) => (
                            <ErrorText>{message}</ErrorText>
                          )}
                        />
                      </FormField>
                    ),
                )}
              </FormBody>

              <BottomRow>
                <AlignedTextButton>Request Update</AlignedTextButton>
              </BottomRow>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </RequestUpgradeWrapper>
  );
};

export default RequestUpdateButton;
